import React, { useMemo, useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { TrendingUpIcon, UsersIcon, AlertTriangleIcon, BarChart3Icon, ActivityIcon, AwardIcon, TargetIcon, Loader2Icon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MOCK_RESULTS, LETGROW_EFFICACY_DETAILED, calculateWeightedPositivityIndex } from '../schemas/mockData';
import type { ResultPayload, WeightedSelection } from '../schemas/types';
import { ChartWrapper } from '../components/ChartWrapper';
import { CHART_COLORS, CHART_GRID, CHART_AXIS, CHART_TOOLTIP, CHART_ANIMATION, formatNumber, formatPercent, customTooltipFormatter } from '../utils/chartConfig';
import { safeForEach } from '../utils/safeArray';
/**
 * Analytics Page
 * Comprehensive analytics with weighted positivity index and efficacy tracking
 * Supports donor/grant reporting for organizations like LetGrow
 */
export function Analytics() {
  const {
    currentOrganization,
    t
  } = useOrganization();
  const [timeRange, setTimeRange] = useState<'30' | '60' | '90' | 'all'>('90');
  const [viewMode, setViewMode] = useState<'overview' | 'efficacy'>('overview');
  const [isLoading, setIsLoading] = useState(false);
  // Filter results by organization and time range
  const orgResults = useMemo(() => {
    const filtered = (Array.isArray(MOCK_RESULTS) ? MOCK_RESULTS : []).filter(r => r.deliveredToSpaces.orgId.includes(currentOrganization.spacesOrgId));
    if (timeRange === 'all') return filtered;
    const daysAgo = parseInt(timeRange);
    const cutoffDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
    return filtered.filter(r => new Date(r.completedAt) > cutoffDate);
  }, [currentOrganization, timeRange]);
  // Calculate weighted positivity index
  const weightedPositivityData = useMemo(() => {
    const userGroups = (Array.isArray(orgResults) ? orgResults : []).reduce((acc, result) => {
      if (!acc[result.externalUserId]) {
        acc[result.externalUserId] = [];
      }
      acc[result.externalUserId].push(result);
      return acc;
    }, {} as Record<string, ResultPayload[]>);
    const userIndices: any[] = [];
    safeForEach('Analytics.userGroups.entries', Object.entries(userGroups), ([userId, results]) => {
      const latestResult = results[results.length - 1];
      const selections: WeightedSelection[] = [{
        imageId: 'img-1',
        weight: latestResult.computed.positiveAffect / 100,
        positivity: 0.8
      }, {
        imageId: 'img-2',
        weight: latestResult.computed.negativeAffect / 100,
        positivity: 0.2
      }];
      userIndices.push({
        userId,
        weightedIndex: calculateWeightedPositivityIndex(selections),
        positiveAffect: latestResult.computed.positiveAffect,
        negativeAffect: latestResult.computed.negativeAffect,
        atRisk: latestResult.atRisk
      });
    });
    return userIndices;
  }, [orgResults]);
  // Calculate aggregate metrics
  const metrics = useMemo(() => {
    const safeOrgResults = Array.isArray(orgResults) ? orgResults : [];
    const total = safeOrgResults.length;
    const atRiskCount = safeOrgResults.filter(r => r.atRisk).length;
    let avgPositive = 0;
    let avgNegative = 0;
    safeForEach('Analytics.metrics.orgResults', safeOrgResults, r => {
      avgPositive += r.computed.positiveAffect;
      avgNegative += r.computed.negativeAffect;
    });
    avgPositive = total > 0 ? avgPositive / total : 0;
    avgNegative = total > 0 ? avgNegative / total : 0;
    const safeWeightedData = Array.isArray(weightedPositivityData) ? weightedPositivityData : [];
    let avgWeightedIndexSum = 0;
    safeForEach('Analytics.metrics.weightedData', safeWeightedData, d => {
      avgWeightedIndexSum += d.weightedIndex;
    });
    const avgWeightedIndex = safeWeightedData.length > 0 ? avgWeightedIndexSum / safeWeightedData.length : 0;
    return {
      totalAssessments: total,
      atRiskCount,
      atRiskPercentage: total > 0 ? Math.round(atRiskCount / total * 100) : 0,
      avgPositiveAffect: Math.round(avgPositive),
      avgNegativeAffect: Math.round(avgNegative),
      avgWeightedPositivity: Math.round(avgWeightedIndex * 100) / 100
    };
  }, [orgResults, weightedPositivityData]);
  // Trend data for charts
  const trendData = useMemo(() => {
    const monthlyData: {
      [month: string]: {
        positive: number[];
        negative: number[];
        weighted: number[];
        count: number;
      };
    } = {};
    const safeOrgResults = Array.isArray(orgResults) ? orgResults : [];
    safeForEach('Analytics.trendData.orgResults', safeOrgResults, result => {
      const month = new Date(result.completedAt).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
      if (!monthlyData[month]) {
        monthlyData[month] = {
          positive: [],
          negative: [],
          weighted: [],
          count: 0
        };
      }
      monthlyData[month].positive.push(result.computed.positiveAffect);
      monthlyData[month].negative.push(result.computed.negativeAffect);
      monthlyData[month].count++;
    });
    return Object.entries(monthlyData).map(([month, data]) => ({
      month,
      avgPositive: Math.round(data.positive.reduce((a, b) => a + b, 0) / data.count),
      avgNegative: Math.round(data.negative.reduce((a, b) => a + b, 0) / data.count),
      assessments: data.count
    })).sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()).slice(-6);
  }, [orgResults]);
  // Distribution data
  const distributionData = useMemo(() => {
    const ranges = [{
      name: '0-20',
      min: 0,
      max: 20,
      count: 0
    }, {
      name: '21-40',
      min: 21,
      max: 40,
      count: 0
    }, {
      name: '41-60',
      min: 41,
      max: 60,
      count: 0
    }, {
      name: '61-80',
      min: 61,
      max: 80,
      count: 0
    }, {
      name: '81-100',
      min: 81,
      max: 100,
      count: 0
    }];
    const safeWeightedData = Array.isArray(weightedPositivityData) ? weightedPositivityData : [];
    safeForEach('Analytics.distributionData.weightedData', safeWeightedData, data => {
      const score = data.weightedIndex * 100;
      const range = ranges.find(r => score >= r.min && score <= r.max);
      if (range) range.count++;
    });
    return ranges;
  }, [weightedPositivityData]);
  const isLetGrow = currentOrganization.id === 'org-letgrow';
  const hasData = orgResults.length > 0;
  return <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-id="element-598">
      <div className="max-w-7xl mx-auto" data-id="element-599">
        {/* Header */}
        <div className="mb-8" data-id="element-600">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4" data-id="element-601">
            <div className="flex items-center gap-3" data-id="element-602">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-603">
                <TrendingUpIcon className="w-7 h-7 text-white" data-id="element-604" />
              </div>
              <div data-id="element-605">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-606">
                  {t.analyticsDashboard}
                </h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1" data-id="element-607">
                  {isLetGrow ? t.programEfficacySubtitle : t.analyticsSubtitle}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-608">
              {isLetGrow && <div className="flex bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm" data-id="element-609">
                  <button onClick={() => setViewMode('overview')} className={`flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${viewMode === 'overview' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-610">
                    {t.overview}
                  </button>
                  <button onClick={() => setViewMode('efficacy')} className={`flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${viewMode === 'efficacy' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-611">
                    {t.efficacyReport}
                  </button>
                </div>}
              <select value={timeRange} onChange={e => setTimeRange(e.target.value as any)} className="w-full sm:w-auto px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all shadow-sm hover:border-gray-300 bg-white" data-id="element-612">
                <option value="30" data-id="element-613">{t.last30Days}</option>
                <option value="60" data-id="element-614">{t.last60Days}</option>
                <option value="90" data-id="element-615">{t.last90Days}</option>
                <option value="all" data-id="element-616">{t.allTime}</option>
              </select>
            </div>
          </div>
        </div>
        {/* Loading State */}
        {isLoading && <div className="flex items-center justify-center py-20" data-id="element-617">
            <div className="text-center" data-id="element-618">
              <Loader2Icon className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" data-id="element-619" />
              <p className="text-gray-600 font-medium" data-id="element-620">{t.loadingAnalytics}</p>
            </div>
          </div>}
        {/* Empty State */}
        {!isLoading && !hasData && <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-12 text-center" data-id="element-621">
            <BarChart3Icon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-622" />
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-623">
              {t.noDataAvailable}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-624">
              {t.noDataMessage}
            </p>
            <button onClick={() => setTimeRange('all')} className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors font-semibold shadow-lg" data-id="element-625">
              {t.viewAllTimeData}
            </button>
          </div>}
        {/* Content - Only show if we have data */}
        {!isLoading && hasData && <>
            {/* LetGrow Efficacy View */}
            {isLetGrow && viewMode === 'efficacy' && <div className="space-y-6" data-id="element-626">
                {/* Program Impact Metrics */}
                <ChartWrapper title={t.programImpactMetrics} subtitle={t.measurableOutcomes} data-id="element-627">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-628">
                    {Object.entries(LETGROW_EFFICACY_DETAILED.programImpact || {}).map(([key, metric]) => <div key={key} className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border-2 border-purple-200 hover:shadow-md transition-shadow" data-id="element-629">
                        <div className="text-sm font-semibold text-purple-900 mb-2" data-id="element-630">
                          {metric.metricName}
                        </div>
                        <div className="flex items-baseline gap-2 mb-3" data-id="element-631">
                          <div className="text-3xl font-bold text-purple-700" data-id="element-632">
                            +{metric.changePercent}%
                          </div>
                          <TrendingUpIcon className="w-5 h-5 text-green-600" data-id="element-633" />
                        </div>
                        <div className="text-xs text-purple-700 space-y-1" data-id="element-634">
                          <div className="flex justify-between" data-id="element-635">
                            <span className="text-purple-600" data-id="element-636">{t.pre}:</span>
                            <span className="font-semibold" data-id="element-637">
                              {metric.preScore}
                            </span>
                          </div>
                          <div className="flex justify-between" data-id="element-638">
                            <span className="text-purple-600" data-id="element-639">{t.post}:</span>
                            <span className="font-semibold" data-id="element-640">
                              {metric.postScore}
                            </span>
                          </div>
                          <div className="pt-2 mt-2 border-t border-purple-300" data-id="element-641">
                            <span className="font-semibold" data-id="element-642">
                              n = {formatNumber(metric.sampleSize)}
                            </span>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </ChartWrapper>
                {/* Longitudinal Progress */}
                <ChartWrapper title={t.longitudinalProgress} subtitle={t.studentDevelopment} height={400} footer={t.dataCollected} data-id="element-643">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-644">
                    <LineChart data={LETGROW_EFFICACY_DETAILED.longitudinalData || []} margin={{
                top: 20,
                right: 30,
                bottom: 40,
                left: 50
              }} data-id="element-645">
                      <CartesianGrid {...CHART_GRID} data-id="element-646" />
                      <XAxis dataKey="month" {...CHART_AXIS} label={{
                  value: t.timePeriod,
                  position: 'insideBottom',
                  offset: -10,
                  style: {
                    ...CHART_AXIS.tick,
                    fontWeight: 600
                  }
                }} data-id="element-647" />
                      <YAxis {...CHART_AXIS} label={{
                  value: t.score,
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    ...CHART_AXIS.tick,
                    fontWeight: 600
                  }
                }} data-id="element-648" />
                      <Tooltip {...CHART_TOOLTIP} formatter={customTooltipFormatter} data-id="element-649" />
                      <Legend wrapperStyle={{
                  paddingTop: '20px'
                }} iconType="line" data-id="element-650" />
                      <Line type="monotone" dataKey="independenceScore" stroke={CHART_COLORS.series[0]} strokeWidth={3} name={t.independence} dot={{
                  r: 4,
                  fill: CHART_COLORS.series[0]
                }} activeDot={{
                  r: 6
                }} animationDuration={CHART_ANIMATION.duration} data-id="element-651" />
                      <Line type="monotone" dataKey="resilienceScore" stroke={CHART_COLORS.series[1]} strokeWidth={3} name={t.resilience} dot={{
                  r: 4,
                  fill: CHART_COLORS.series[1]
                }} activeDot={{
                  r: 6
                }} animationDuration={CHART_ANIMATION.duration} data-id="element-652" />
                      <Line type="monotone" dataKey="confidenceScore" stroke={CHART_COLORS.success} strokeWidth={3} name={t.confidence} dot={{
                  r: 4,
                  fill: CHART_COLORS.success
                }} activeDot={{
                  r: 6
                }} animationDuration={CHART_ANIMATION.duration} data-id="element-653" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
                {/* Reach & Outcomes */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-654">
                  <ChartWrapper title={t.programReach} subtitle={t.scaleAndDistribution} data-id="element-655">
                    <div className="space-y-3" data-id="element-656">
                      {[{
                  label: t.totalStudents,
                  value: 900,
                  color: 'blue'
                }, {
                  label: t.schoolsServed,
                  value: 4,
                  color: 'green'
                }, {
                  label: t.activeCohorts,
                  value: 48,
                  color: 'purple'
                }, {
                  label: t.citiesReached,
                  value: 4,
                  color: 'orange'
                }].map(item => <div key={item.label} className={`flex justify-between items-center p-4 bg-${item.color}-50 rounded-xl border-2 border-${item.color}-200 hover:shadow-md transition-all`} data-id="element-657">
                          <span className="text-sm font-semibold text-gray-700" data-id="element-658">
                            {item.label}
                          </span>
                          <span className={`text-2xl font-bold text-${item.color}-600`} data-id="element-659">
                            {typeof item.value === 'number' ? formatNumber(item.value) : item.value}
                          </span>
                        </div>)}
                    </div>
                  </ChartWrapper>
                  <ChartWrapper title={t.outcomes} subtitle={t.successMetrics} data-id="element-660">
                    <div className="space-y-3" data-id="element-661">
                      {[{
                  label: t.completionRate,
                  value: 89.7,
                  color: 'green'
                }, {
                  label: t.positiveChange,
                  value: 87.2,
                  color: 'blue'
                }, {
                  label: t.sustainedImprovement,
                  value: 78.5,
                  color: 'purple'
                }, {
                  label: t.teacherSatisfaction,
                  value: 91.4,
                  color: 'orange'
                }].map(item => <div key={item.label} className={`flex justify-between items-center p-4 bg-${item.color}-50 rounded-xl border-2 border-${item.color}-200 hover:shadow-md transition-all`} data-id="element-662">
                          <span className="text-sm font-semibold text-gray-700" data-id="element-663">
                            {item.label}
                          </span>
                          <span className={`text-2xl font-bold text-${item.color}-600`} data-id="element-664">
                            {formatPercent(item.value)}
                          </span>
                        </div>)}
                    </div>
                  </ChartWrapper>
                </div>
              </div>}
            {/* Standard Overview */}
            {(!isLetGrow || viewMode === 'overview') && <>
                {/* Key Metrics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8" data-id="element-665">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200 hover:shadow-lg transition-all" data-id="element-666">
                    <div className="flex items-center justify-between mb-4" data-id="element-667">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-md" data-id="element-668">
                        <BarChart3Icon className="w-6 h-6 text-white" data-id="element-669" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-blue-700 mb-1" data-id="element-670">
                      {formatNumber(metrics.totalAssessments)}
                    </div>
                    <div className="text-sm font-semibold text-blue-900 mb-2" data-id="element-671">
                      {t.totalAssessments}
                    </div>
                    <div className="text-xs text-blue-700" data-id="element-672">
                      {t.completedInPeriod}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200 hover:shadow-lg transition-all" data-id="element-673">
                    <div className="flex items-center justify-between mb-4" data-id="element-674">
                      <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center shadow-md" data-id="element-675">
                        <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-676" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-green-700 mb-1" data-id="element-677">
                      {metrics.avgPositiveAffect}
                    </div>
                    <div className="text-sm font-semibold text-green-900 mb-2" data-id="element-678">
                      {t.avgPositiveAffect}
                    </div>
                    <div className="text-xs text-green-700" data-id="element-679">
                      {t.meanPositivityScore}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200 hover:shadow-lg transition-all" data-id="element-680">
                    <div className="flex items-center justify-between mb-4" data-id="element-681">
                      <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center shadow-md" data-id="element-682">
                        <ActivityIcon className="w-6 h-6 text-white" data-id="element-683" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-purple-700 mb-1" data-id="element-684">
                      {metrics.avgWeightedPositivity}
                    </div>
                    <div className="text-sm font-semibold text-purple-900 mb-2" data-id="element-685">
                      {t.weightedPositivity}
                    </div>
                    <div className="text-xs text-purple-700" data-id="element-686">
                      {t.accountsForWeight}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border-2 border-red-200 hover:shadow-lg transition-all" data-id="element-687">
                    <div className="flex items-center justify-between mb-4" data-id="element-688">
                      <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center shadow-md" data-id="element-689">
                        <AlertTriangleIcon className="w-6 h-6 text-white" data-id="element-690" />
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-red-700 mb-1" data-id="element-691">
                      {formatPercent(metrics.atRiskPercentage, 0)}
                    </div>
                    <div className="text-sm font-semibold text-red-900 mb-2" data-id="element-692">
                      {t.atRiskRate}
                    </div>
                    <div className="text-xs text-red-700" data-id="element-693">
                      {formatNumber(metrics.atRiskCount)}{' '}
                      {t.participantsFlagged}
                    </div>
                  </div>
                </div>
                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-694">
                  {/* Trend Chart */}
                  <ChartWrapper title={t.affectTrends} subtitle={t.monthlyAverages} height={350} footer={t.measuredScale} data-id="element-695">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-696">
                      <LineChart data={trendData} margin={{
                  top: 20,
                  right: 30,
                  bottom: 40,
                  left: 50
                }} data-id="element-697">
                        <CartesianGrid {...CHART_GRID} data-id="element-698" />
                        <XAxis dataKey="month" {...CHART_AXIS} label={{
                    value: t.month,
                    position: 'insideBottom',
                    offset: -10,
                    style: {
                      ...CHART_AXIS.tick,
                      fontWeight: 600
                    }
                  }} data-id="element-699" />
                        <YAxis {...CHART_AXIS} label={{
                    value: t.affectScore,
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      ...CHART_AXIS.tick,
                      fontWeight: 600
                    }
                  }} data-id="element-700" />
                        <Tooltip {...CHART_TOOLTIP} formatter={customTooltipFormatter} data-id="element-701" />
                        <Legend wrapperStyle={{
                    paddingTop: '20px'
                  }} iconType="line" data-id="element-702" />
                        <Line type="monotone" dataKey="avgPositive" stroke={CHART_COLORS.positive} strokeWidth={3} name={t.positiveAffect} dot={{
                    r: 4,
                    fill: CHART_COLORS.positive
                  }} activeDot={{
                    r: 6
                  }} animationDuration={CHART_ANIMATION.duration} data-id="element-703" />
                        <Line type="monotone" dataKey="avgNegative" stroke={CHART_COLORS.negative} strokeWidth={3} name={t.negativeAffect} dot={{
                    r: 4,
                    fill: CHART_COLORS.negative
                  }} activeDot={{
                    r: 6
                  }} animationDuration={CHART_ANIMATION.duration} data-id="element-704" />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartWrapper>
                  {/* Distribution Chart */}
                  <ChartWrapper title={t.weightedDistribution} subtitle={t.participantDistribution} height={350} footer={t.higherScores} data-id="element-705">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-706">
                      <BarChart data={distributionData} margin={{
                  top: 20,
                  right: 30,
                  bottom: 40,
                  left: 50
                }} data-id="element-707">
                        <CartesianGrid {...CHART_GRID} data-id="element-708" />
                        <XAxis dataKey="name" {...CHART_AXIS} label={{
                    value: t.scoreRange,
                    position: 'insideBottom',
                    offset: -10,
                    style: {
                      ...CHART_AXIS.tick,
                      fontWeight: 600
                    }
                  }} data-id="element-709" />
                        <YAxis {...CHART_AXIS} label={{
                    value: t.participants,
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      ...CHART_AXIS.tick,
                      fontWeight: 600
                    }
                  }} data-id="element-710" />
                        <Tooltip {...CHART_TOOLTIP} formatter={customTooltipFormatter} data-id="element-711" />
                        <Bar dataKey="count" fill={CHART_COLORS.primary} name={t.participants} radius={[4, 4, 0, 0]} animationDuration={CHART_ANIMATION.duration} data-id="element-712" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartWrapper>
                </div>
                {/* Assessment Volume */}
                <ChartWrapper title={t.assessmentVolume} subtitle={t.totalCompleted} height={350} footer={t.trackingEngagement} data-id="element-713">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-714">
                    <BarChart data={trendData} margin={{
                top: 20,
                right: 30,
                bottom: 40,
                left: 50
              }} data-id="element-715">
                      <CartesianGrid {...CHART_GRID} data-id="element-716" />
                      <XAxis dataKey="month" {...CHART_AXIS} label={{
                  value: t.month,
                  position: 'insideBottom',
                  offset: -10,
                  style: {
                    ...CHART_AXIS.tick,
                    fontWeight: 600
                  }
                }} data-id="element-717" />
                      <YAxis {...CHART_AXIS} label={{
                  value: t.totalAssessments,
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    ...CHART_AXIS.tick,
                    fontWeight: 600
                  }
                }} data-id="element-718" />
                      <Tooltip {...CHART_TOOLTIP} formatter={customTooltipFormatter} data-id="element-719" />
                      <Bar dataKey="assessments" fill={CHART_COLORS.primary} name={t.completedAssessments} radius={[4, 4, 0, 0]} animationDuration={CHART_ANIMATION.duration} data-id="element-720" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </>}
          </>}
      </div>
    </div>;
}